import { supabase } from './supabase/client';
import { buildOptimizedQuery, executePagedQuery, PaginationOptions, FilterOptions, bulkInsert } from '../utils/queryOptimization';
import { handleSupabaseError } from '../utils/errorHandling';
import { v4 as uuidv4 } from 'uuid';
import { Transaction as BaseTransaction } from '@flows.cash/types';

// Define transaction types
export enum TransactionType {
  EXPENSE = 'expense',
  INCOME = 'income',
  TRANSFER = 'transfer'
}

// Interface for transaction data, extending the base Transaction type
export interface Transaction extends Omit<BaseTransaction, 'currency'> {
  currency_code: string;
}

// Interface for creating a new transaction
export interface CreateTransactionData {
  account_id: string;
  amount: number;
  category_id?: string;
  date: string;
  description: string;
  notes?: string;
  type: TransactionType;
  payee_id?: string;
  tags?: string[];
  to_account_id?: string; // For transfers
  status: 'pending' | 'completed' | 'cancelled';
  is_recurring?: boolean;
  attachment_ids?: string[];
  currency_code: string;
  original_amount?: number;
}

/**
 * Service for managing transactions
 */
export const transactionService = {
  /**
   * Get transactions with filtering and pagination
   */
  async getTransactions(
    userId: string,
    pagination: PaginationOptions = {},
    filters: FilterOptions[] = []
  ) {
    try {
      // Columns to select for better performance
      const columns = `
        id, 
        amount, 
        description, 
        date, 
        category_id, 
        account_id, 
        payee_id, 
        notes, 
        type, 
        tags, 
        created_at, 
        updated_at, 
        currency_code, 
        original_amount
      `;
      
      // Build the optimized query
      const query = buildOptimizedQuery(
        supabase,
        'transactions',
        columns,
        pagination,
        filters,
        userId
      );
      
      // Execute the query
      const result = await executePagedQuery(query);
      
      return {
        transactions: result.data as Transaction[],
        totalCount: result.count
      };
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw handleSupabaseError(error);
    }
  },
  
  /**
   * Get a single transaction by ID
   */
  async getTransaction(userId: string, transactionId: string) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('id', transactionId)
        .single();
      
      if (error) throw error;
      if (!data) throw new Error('Transaction not found');
      
      return data as Transaction;
    } catch (error) {
      console.error(`Error fetching transaction ${transactionId}:`, error);
      throw handleSupabaseError(error);
    }
  },
  
  /**
   * Create a new transaction
   */
  async createTransaction(userId: string, transactionData: Omit<CreateTransactionData, 'status'> & { status?: 'pending' | 'completed' | 'cancelled' }): Promise<Transaction> {
    try {
      const transaction = {
        ...transactionData,
        user_id: userId,
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: transactionData.status || 'completed' // Provide default if not specified
      };
      
      // Insert the transaction
      const { data, error } = await supabase
        .from('transactions')
        .insert([transaction])
        .select()
        .single();
      
      if (error) throw error;
      if (!data) throw new Error('Failed to create transaction');
      
      return data as Transaction;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw handleSupabaseError(error);
    }
  },
  
  /**
   * Update an existing transaction
   */
  async updateTransaction(
    userId: string, 
    transactionId: string, 
    transactionData: Partial<Transaction>
  ): Promise<Transaction> {
    try {
      // Remove properties that shouldn't be updated
      const { id, user_id, created_at, ...updateData } = transactionData as any;
      
      // Add updated_at timestamp
      const dataToUpdate = {
        ...updateData,
        updated_at: new Date().toISOString()
      };
      
      // Update the transaction
      const { data, error } = await supabase
        .from('transactions')
        .update(dataToUpdate)
        .eq('user_id', userId)
        .eq('id', transactionId)
        .select()
        .single();
      
      if (error) throw error;
      if (!data) throw new Error('Transaction not found');
      
      return data as Transaction;
    } catch (error) {
      console.error(`Error updating transaction ${transactionId}:`, error);
      throw handleSupabaseError(error);
    }
  },
  
  /**
   * Delete a transaction
   */
  async deleteTransaction(userId: string, transactionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('user_id', userId)
        .eq('id', transactionId);
      
      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting transaction ${transactionId}:`, error);
      throw handleSupabaseError(error);
    }
  },
  
  /**
   * Bulk delete transactions with optimized server-side processing
   */
  async bulkDeleteTransactions(
    userId: string, 
    transactionIds: string[],
    onProgress?: (current: number, total: number, errors: string[]) => void
  ): Promise<{
    deletedIds: string[];
    failedIds: string[];
    errors: string[];
  }> {
    if (!transactionIds || transactionIds.length === 0) {
      return { deletedIds: [], failedIds: [], errors: [] };
    }

    try {
      console.log(`Starting bulk delete for ${transactionIds.length} transactions`);
      
      // For very large operations (>200), use the database function for maximum efficiency
      if (transactionIds.length > 200) {
        return await this.bulkDeleteWithDatabaseFunction(userId, transactionIds);
      }
      
      const deletedIds: string[] = [];
      const failedIds: string[] = [];
      const errors: string[] = [];
      
      // For large operations (>100), process in smaller chunks to avoid timeouts
      const CHUNK_SIZE = transactionIds.length > 100 ? 50 : transactionIds.length;
      
      for (let i = 0; i < transactionIds.length; i += CHUNK_SIZE) {
        const chunk = transactionIds.slice(i, i + CHUNK_SIZE);
        console.log(`Processing chunk ${Math.floor(i / CHUNK_SIZE) + 1}/${Math.ceil(transactionIds.length / CHUNK_SIZE)}: ${chunk.length} transactions`);
        
        // Update progress before processing chunk
        if (onProgress) {
          onProgress(i, transactionIds.length, errors);
        }
        
        try {
          // First, get all transactions in this chunk to handle transfer relationships
          const { data: transactions, error: fetchError } = await supabase
            .from('transactions')
            .select('id, transfer_id, metadata, account_id')
            .eq('user_id', userId)
            .in('id', chunk);

          if (fetchError) {
            console.error('Error fetching transactions for bulk delete:', fetchError);
            errors.push(`Failed to fetch transactions: ${fetchError.message}`);
            failedIds.push(...chunk);
            continue;
          }

          if (!transactions || transactions.length === 0) {
            console.log('No transactions found for chunk');
            failedIds.push(...chunk);
            continue;
          }

          // Collect all transaction IDs that need to be deleted (including linked transfers)
          const allIdsToDelete = new Set<string>();
          const affectedAccounts = new Set<string>();

          for (const transaction of transactions) {
            allIdsToDelete.add(transaction.id);
            affectedAccounts.add(transaction.account_id);

            // Handle transfer relationships
            if (transaction.transfer_id) {
              // Get all linked transactions for this transfer
              const { data: linkedTransactions } = await supabase
                .from('transactions')
                .select('id, account_id')
                .eq('transfer_id', transaction.transfer_id)
                .eq('user_id', userId);

              if (linkedTransactions) {
                linkedTransactions.forEach(linked => {
                  allIdsToDelete.add(linked.id);
                  affectedAccounts.add(linked.account_id);
                });
              }
            }
            // Handle legacy transfer metadata
            else if (transaction.metadata?.isTransfer && transaction.metadata?.linkedTransactionId) {
              allIdsToDelete.add(transaction.metadata.linkedTransactionId);
              // We'd need to fetch the linked transaction to get its account_id
            }
          }

          // Perform the bulk delete operation
          const idsArray = Array.from(allIdsToDelete);
          const { error: deleteError } = await supabase
            .from('transactions')
            .delete()
            .eq('user_id', userId)
            .in('id', idsArray);

          if (deleteError) {
            console.error('Error in bulk delete operation:', deleteError);
            errors.push(`Failed to delete chunk: ${deleteError.message}`);
            failedIds.push(...chunk);
          } else {
            deletedIds.push(...chunk);
            console.log(`Successfully deleted ${idsArray.length} transactions (including linked transfers)`);
          }

        } catch (chunkError) {
          console.error(`Error processing chunk:`, chunkError);
          errors.push(`Chunk processing failed: ${chunkError instanceof Error ? chunkError.message : 'Unknown error'}`);
          failedIds.push(...chunk);
        }
        
        // Update progress after processing chunk
        if (onProgress) {
          onProgress(Math.min(i + CHUNK_SIZE, transactionIds.length), transactionIds.length, errors);
        }
      }

      console.log(`Bulk delete completed: ${deletedIds.length} successful, ${failedIds.length} failed`);
      
      return {
        deletedIds,
        failedIds,
        errors
      };

    } catch (error) {
      console.error('Error in bulk delete transactions:', error);
      throw handleSupabaseError(error);
    }
  },

  /**
   * Use database function for very large bulk deletes (>200 transactions)
   */
  async bulkDeleteWithDatabaseFunction(userId: string, transactionIds: string[]): Promise<{
    deletedIds: string[];
    failedIds: string[];
    errors: string[];
  }> {
    try {
      console.log(`Using database function for bulk delete of ${transactionIds.length} transactions`);
      
      const { data, error } = await supabase.rpc('bulk_delete_user_transactions', {
        p_user_id: userId,
        p_transaction_ids: transactionIds
      });

      if (error) {
        console.error('Database function error:', error);
        return {
          deletedIds: [],
          failedIds: transactionIds,
          errors: [`Database function failed: ${error.message}`]
        };
      }

      // Extract the successfully deleted IDs from the function result
      const deletedIds = data?.map((row: any) => row.deleted_id) || [];
      const failedIds = transactionIds.filter(id => !deletedIds.includes(id));

      console.log(`Database function completed: ${deletedIds.length} deleted, ${failedIds.length} failed`);

      return {
        deletedIds,
        failedIds,
        errors: failedIds.length > 0 ? [`${failedIds.length} transactions could not be deleted`] : []
      };

    } catch (error) {
      console.error('Error in database function bulk delete:', error);
      return {
        deletedIds: [],
        failedIds: transactionIds,
        errors: [`Database function execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`]
      };
    }
  },
  
  /**
   * Bulk insert transactions (optimized for large datasets)
   */
  async bulkInsertTransactions(
    userId: string, 
    transactions: Partial<CreateTransactionData>[]
  ): Promise<Transaction[]> {
    try {
      // Prepare transactions with user_id and ensure status is valid
      const transactionsToInsert = transactions.map(transaction => ({
        ...transaction,
        user_id: userId,
        id: uuidv4(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: transaction.status || 'completed'
      }));
      
      // Use the bulkInsert utility
      const { data, error } = await bulkInsert<'transactions'>(
        supabase,
        'transactions',
        transactionsToInsert as any,
        500 // Chunk size
      );
      
      if (error) throw error;
      
      // Cast the result to our Transaction type
      return (data || []) as unknown as Transaction[];
    } catch (error) {
      console.error('Error bulk inserting transactions:', error);
      throw handleSupabaseError(error);
    }
  }
}; 